import { combineEpics, ofType, StateObservable } from "redux-observable"
import { from, concat, of, EMPTY } from "rxjs"
import { flatMap, catchError, filter, switchMap } from "rxjs/operators"
import { Epic } from "../../epics"

import { GlobalState } from "../../store"
import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import { requestWelcomePageGrid } from "../welcomepage/actions"
import { RequestCancelledError } from "../../errors"

export const defaultAddingBookmarkErrorMessage =
    "Something went wrong when adding the bookmark."
export const defaultRemovingBookmarkErrorMessage =
    "Something went wrong when removing the bookmark."
export const defaultInitializingBookmarkButtonErrorMessage =
    "Something went wrong when trying to initialize the bookmark button."
export const defaultRequestingBookmarksErrorMessage =
    "Something went wrong when fetching bookmarks."
export const defaultSavingBookmarkTitleErrorMessage =
    "Something went wrong when saving the bookmark title."

export const initializeBookmarksEpics: Epic = (
    action$,
    state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter(() => !state$.value.bookmarks.initialized),
        flatMap(() =>
            concat(
                of(actions.requestBookmarks()),
                of(actions.setInitialized()),
            ),
        ),
    )

export const requestBookmarksEpic: Epic = (
    action$,
    state$,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_BOOKMARKS),
        switchMap(() => {
            const {
                bookmarks: { filter },
            } = state$.value
            return from(bookmarkAPI.getBookmarks(null, filter)).pipe(
                flatMap(({ data }) => of(actions.receiveBookmarks(data))),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(actions.receiveBookmarks([])),
                        of(
                            addErrorNotification(
                                defaultRequestingBookmarksErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const addBookmarkEpic: Epic = (
    action$,
    _state$,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.ADD_BOOKMARK),
        switchMap(({ bookmarkButton }: actions.BookmarkButtonAction) => {
            const {
                bookmarkButtonId,
                resourceType,
                resourceId,
                parentItemId,
                workspaceId,
                url,
            } = bookmarkButton
            if (resourceId === undefined || resourceId === null) {
                return EMPTY
            }

            return from(
                bookmarkAPI.addBookmark(
                    resourceType,
                    resourceId,
                    url,
                    workspaceId,
                    parentItemId,
                ),
            ).pipe(
                flatMap((bookmarkId: number) =>
                    concat(
                        of(
                            actions.receiveBookmarkButtonBookmarkId(
                                bookmarkButtonId,
                                bookmarkId,
                                false,
                            ),
                        ),
                        of(actions.requestBookmarks()),
                        of(requestWelcomePageGrid()),
                    ),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveBookmarkButtonBookmarkId(
                                bookmarkButtonId,
                                null,
                                false,
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultAddingBookmarkErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const unbookmarkEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.UNBOOKMARK),
        flatMap(({ bookmarkButton }: actions.BookmarkButtonAction) => {
            const { bookmarkId, bookmarkButtonId } = bookmarkButton
            if (!bookmarkId) {
                return EMPTY
            }

            return from(bookmarkAPI.removeBookmark(bookmarkId)).pipe(
                flatMap(() =>
                    concat(
                        of(actions.receiveRemoveBookmark(bookmarkId)),
                        of(
                            actions.receiveBookmarkButtonBookmarkId(
                                bookmarkButtonId,
                                null,
                                false,
                            ),
                        ),
                    ),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(
                        addErrorNotification(
                            defaultRemovingBookmarkErrorMessage,
                        ),
                    )
                }),
            )
        }),
    )

export const initializeBookmarkButtonEpic: Epic = (
    action$,
    _state$,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.INITIALIZE_BOOKMARK_BUTTON),
        switchMap(
            ({
                bookmarkButtonId,
                resourceType,
                resourceId,
                parentItemId,
            }: actions.InitializeBookmarkButton) => {
                if (resourceId === undefined || resourceId === null) {
                    return EMPTY
                }

                return from(
                    bookmarkAPI.findBookmark(
                        resourceType,
                        resourceId,
                        parentItemId,
                    ),
                ).pipe(
                    flatMap((bookmarkId?: number) =>
                        !!bookmarkId
                            ? of(
                                  actions.receiveBookmarkButtonBookmarkId(
                                      bookmarkButtonId,
                                      bookmarkId,
                                      false,
                                  ),
                              )
                            : of(
                                  actions.receiveBookmarkButtonBookmarkId(
                                      bookmarkButtonId,
                                      null,
                                      false,
                                  ),
                              ),
                    ),
                    catchError((error: Response | Error) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        return concat(
                            of(
                                actions.receiveBookmarkButtonBookmarkId(
                                    bookmarkButtonId,
                                    null,
                                    false,
                                ),
                            ),
                            of(
                                addErrorNotification(
                                    defaultInitializingBookmarkButtonErrorMessage,
                                ),
                            ),
                        )
                    }),
                )
            },
        ),
    )

export const saveBookmarkTitleEpic: Epic = (
    action$,
    _state$,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.SAVE_BOOKMARK_TITLE),
        switchMap(({ bookmark }: actions.BookmarkAction) => {
            const { bookmarkId, title } = bookmark
            return from(bookmarkAPI.saveBookmarkTitle(bookmarkId, title)).pipe(
                flatMap(() => of(actions.receiveBookmark(bookmark))),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(
                        addErrorNotification(
                            defaultSavingBookmarkTitleErrorMessage,
                        ),
                    )
                }),
            )
        }),
    )

export const removeBookmarkEpic: Epic = (
    action$,
    _state$,
    { bookmarks: { bookmarkAPI } },
) =>
    action$.pipe(
        ofType(actions.REMOVE_BOOKMARK),
        flatMap(({ bookmarkId }: actions.BookmarkIdAction) => {
            return from(bookmarkAPI.removeBookmark(bookmarkId)).pipe(
                flatMap(() => of(actions.receiveRemoveBookmark(bookmarkId))),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(
                        addErrorNotification(
                            defaultRemovingBookmarkErrorMessage,
                        ),
                    )
                }),
            )
        }),
    )

export default combineEpics(
    initializeBookmarksEpics,
    requestBookmarksEpic,
    initializeBookmarkButtonEpic,
    addBookmarkEpic,
    saveBookmarkTitleEpic,
    removeBookmarkEpic,
    unbookmarkEpic,
)
